import Sidebar from '@/components/layout/Sidebar';
import NavigationRail from '@/components/layout/NavigationRail';
import BootstrapGate from '@/components/BootstrapGate';
import { SuspensionGate } from '@/features/suspension/components/SuspensionGate';
import { AssistantSheet } from '@/features/ai-assistant/components/AssistantSheet';
import { useAiChatStore } from '@/features/ai-assistant/stores/ai-chat.store';
import { featureFlags } from '@/featureFlags';
import { useAiAssistantAvailable } from '@/features/ai-assistant/hooks/useAiAssistantAvailable';
import { useT } from '@/i18n';
import { usePendingOrdersCount } from '@/features/orders/hooks/useOrders';
import { usePendingReservations } from '@/features/reservations/hooks/useReservations';
import { useUnreadCount } from '@/features/notifications/hooks/useInbox';
import { usePushRegistration } from '@/features/notifications/hooks/usePushRegistration';
import { useReservationsRealtime } from '@/features/reservations/hooks/useReservationsRealtime';
import { useOrdersRealtime } from '@/features/orders/hooks/useOrdersRealtime';
import routes from '@/routes';
import { useAuthStore } from '@/stores/auth.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useUiStore } from '@/stores/ui.store';
import { installActionAnchor } from '@/utils/actionAnchor';
import { installDialogLocale } from '@/utils/dialogLocale';
import { installAndroidBackButton } from '@/utils/androidBackButton';
import { installAppResume } from '@/utils/appResume';
import { installMobileToastAsDialog } from '@/lib/mobile-toast';
import { screenTracking } from '@/lib/analytics/screen-tracking';
import { mdNavbarElevation } from '@/utils/useMdNavbarElevation';
import SettingsPage from '@/features/settings/pages/SettingsPage';
import { brandColors } from '@/theme/brand-colors';
import { App, Link, Popup, Toolbar, ToolbarPane, View, Views, f7 } from 'framework7-react';
import type { Framework7Parameters } from 'framework7/types';
import { Capacitor } from '@capacitor/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

const BREAKPOINT = 768;

// Framework7 theme selection. The brand layer in theme/restaurant-theme.css
// paints both bases identically; the base only decides the platform CHROME
// (iOS glass bubble vs Material 3 rail / nav bar).
//
// On NATIVE the theme follows the OS, decided at RUNTIME from the Capacitor
// platform — not a build-time env var. This matters because the mobile app runs
// a single shared web bundle delivered over-the-air (Capgo), so a build-time
// `VITE_F7_THEME` couldn't be both "ios" and "md" at once. `Capacitor.getPlatform()`
// is injected by the native shell before this module evaluates (and before F7
// init, which locks the theme), so the same OTA bundle themes itself per-OS:
// Android → md, iOS → ios.
//
// On WEB there's no native platform, so we keep the `VITE_F7_THEME` env var
// ("ios" | "md", default "ios") with a `?theme=ios|md` query override for
// testing. NOTE: F7 locks the theme at init, so a query/env switch needs a
// page reload to re-theme.
const platform = Capacitor.getPlatform();
const envTheme = import.meta.env.VITE_F7_THEME;
const webDefaultTheme = envTheme === 'ios' || envTheme === 'md' ? envTheme : 'ios';
const themeParam = new URLSearchParams(window.location.search).get('theme');
const webTheme = themeParam === 'ios' || themeParam === 'md' ? themeParam : webDefaultTheme;
const theme = platform === 'android' ? 'md' : platform === 'ios' ? 'ios' : webTheme;

// Two distinct platform skins for the tablet/desktop chrome: iOS (`?theme=ios`)
// gets the floating translucent glass "bubble" Sidebar; the Android `md` base
// (the default everywhere else) gets a Material 3 navigation rail. The rail
// occupies real layout space with the workspace beside it, so the layout wraps
// it differently (`--rail` modifier) - see styles.css.
const isIosChrome = theme === 'ios';

const f7params: Framework7Parameters = {
  name: 'Restaurant Dashboard',
  theme,
  routes,
  on: {
    ...mdNavbarElevation,
    ...screenTracking,
  },
  // Global toast default: pin every toast to the bottom-RIGHT corner. Call sites
  // already pass `position: 'bottom'` but leave `horizontalPosition` unset, so
  // this default applies everywhere without touching each call.
  toast: {
    horizontalPosition: 'right',
  },
  // HIG (Nhóm E): action sheets become a popover anchored to the trigger on
  // regular-width (iPad/desktop). This only converts when the call passes a
  // `targetEl`; targetless calls stay as a bottom action sheet (correct on phone).
  actions: {
    convertToPopover: true,
  },
  // F7 v9's iOS 26 touch-highlight scales matched elements to 1.05 on press.
  // Its default selector includes `.subnavbar`, so touch-dragging a *scrollable*
  // subnavbar (our reservations date strip) triggers the scale on every scroll.
  // F7 already excludes `.tabbar-scrollable` for the same reason but not our
  // strip - so we add a `.no-touch-highlight` opt-out and tag that subnavbar.
  // (Selector mirrors the F7 v9 default with `.subnavbar` → `.subnavbar:not(.no-touch-highlight)`.)
  touch: {
    touchHighlightElements:
      '.toolbar-pane, .navbar .left, .navbar .right, .actions-group:not(.actions-grid .actions-group), .searchbar-input-wrap, .searchbar-disable-button, .subnavbar:not(.no-touch-highlight), .searchbar-input-wrap .autocomplete-dropdown, .messagebar-area, .notification, .toast, .fab > a',
  },
};

function TabBar({ startOnSettings }: { startOnSettings: boolean }) {
  const t = useT();
  const { data: pending } = usePendingReservations();
  const pendingCount = Array.isArray(pending) ? pending.length : 0;
  const { data: pendingOrders } = usePendingOrdersCount();
  const pendingOrdersCount = pendingOrders ?? 0;
  const { data: unread } = useUnreadCount();
  const unreadCount = unread?.count ?? 0;
  // AI assistant takes the Customers slot on mobile when it's enabled for this
  // restaurant (Customers moves into Settings). Same gate as the AssistantSheet mount.
  const showAi = useAiAssistantAvailable();

  return (
    <Toolbar tabbar icons bottom>
      <ToolbarPane>
        <Link
          tabLink="#view-reservations"
          data-testid="tab-reservations"
          tabLinkActive={!startOnSettings}
          tabbarLabel
          iconIos="f7:calendar_fill"
          iconMd="material:event"
          text={t.nav.reservations}
          iconBadge={pendingCount > 0 ? pendingCount : undefined}
          badgeColor="red"
        />
        <Link
          tabLink="#view-orders"
          // Stable handle for journey tests: the unread badge is the only
          // orders signal visible from any other tab.
          data-testid="tab-orders"
          tabbarLabel
          iconIos="f7:bag_fill"
          iconMd="material:shopping_bag"
          text={t.nav.orders}
          iconBadge={pendingOrdersCount > 0 ? pendingOrdersCount : undefined}
          badgeColor="red"
        />
        {featureFlags.tableOrdering && (
          <Link
            tabLink="#view-tables"
            tabbarLabel
            iconIos="f7:square_grid_2x2_fill"
            iconMd="material:table_restaurant"
            text={t.nav.tables}
          />
        )}
        {/* AI assistant opens the AssistantSheet (mounted at the app shell) rather
            than switching tabs - so no tabLink, just an action. Customers moved
            into Settings. When AI is off, this slot is simply absent. */}
        {showAi && (
          <Link
            // Not a real tab (opens the AssistantSheet, no tabLink), but it still needs
            // the `tab-link` class so Framework7 centers its icon/label like the
            // siblings AND counts it when sizing the active-tab highlight - without
            // it the icon sits off-centre and the highlight is one cell too wide.
            className="tab-link"
            href={false}
            data-testid="tab-ai-assistant"
            tabbarLabel
            iconIos="f7:sparkles"
            iconMd="material:auto_awesome"
            text={t.nav.aiAssistant}
            onClick={() => useAiChatStore.getState().open()}
          />
        )}
        <Link
          tabLink="#view-settings"
          tabLinkActive={startOnSettings}
          tabbarLabel
          iconIos="f7:gear_alt_fill"
          iconMd="material:settings"
          text={t.nav.settings}
          iconBadge={featureFlags.inbox && unreadCount > 0 ? unreadCount : undefined}
          badgeColor="red"
        />
      </ToolbarPane>
    </Toolbar>
  );
}

function MobileTabs() {
  const [startOnSettings] = useState(() => {
    const target = useUiStore.getState().settingsTabRestaurantId;
    return target !== null && target === useRestaurantStore.getState().selectedRestaurantId;
  });

  useEffect(() => {
    useUiStore.getState().setSettingsTabRestaurantId(null);
  }, []);

  return (
    <Views tabs>
      <TabBar startOnSettings={startOnSettings} />
      {/* No `browserHistory` on mobile: tabs are independent View
          stacks and F7 doesn't change the URL on tab switch, so syncing
          one tab to the address bar only causes that tab to adopt stale
          cross-section URLs (e.g. a leftover /settings/... path). Native
          tabbed apps have no URL bar anyway - let each tab own its url. */}
      <View id="view-reservations" main tab tabActive={!startOnSettings} url="/reservation" />
      <View id="view-orders" tab url="/orders" />
      {featureFlags.tableOrdering && <View id="view-tables" tab url="/tables" />}
      <View id="view-settings" tab tabActive={startOnSettings} url="/settings" />
    </Views>
  );
}

function AppComponent() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const selectedRestaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  // AI assistant is hidden entirely unless it's activated for this restaurant
  // (managed in the partner portal via Restaurant.aiChatEnabled).
  const aiAvailable = useAiAssistantAvailable();
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const settingsOpen = useUiStore((s) => s.settingsOpen);
  const setSettingsOpen = useUiStore((s) => s.setSettingsOpen);
  const [isMobile, setIsMobile] = useState(window.innerWidth < BREAKPOINT);
  // Keep the Settings shell mounted for the duration of the popup's close
  // animation, then tear it down - so the sheet never slides away empty and its
  // queries don't run while it's closed. Turns on with `settingsOpen`; off only
  // once `onPopupClosed` fires (after the slide-down completes).
  const [settingsMounted, setSettingsMounted] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < BREAKPOINT);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (settingsOpen) setSettingsMounted(true);
  }, [settingsOpen]);

  // Reconcile the desktop-only Settings popup when the viewport flips to mobile.
  // The popup has no place in the mobile tab layout, so close it and carry the
  // user into the Settings TAB (where Settings lives as a page) instead of
  // dropping them back on Reservations.
  useEffect(() => {
    if (!isMobile || !settingsOpen) return;
    setSettingsOpen(false);
    setSettingsMounted(false);
    // Defer to the next frame so the mobile tab Views are mounted before we show.
    requestAnimationFrame(() => f7.tab?.show?.('#view-settings'));
  }, [isMobile, settingsOpen, setSettingsOpen]);

  // HIG (Nhóm E): anchor action-sheet→popover conversion to the pressed control.
  // Must run after <App> has initialised F7 (f7ready needs the event system), so
  // it lives in an effect rather than at module load.
  useEffect(() => {
    installActionAnchor();
    installDialogLocale();
    // On the mobile-phone layout, surface every toast as a dismiss-on-OK dialog
    // instead of a fleeting toast (desktop/tablet keep toasts).
    installMobileToastAsDialog();
    // Wire the Android hardware/gesture back button to F7's router (native only).
    installAndroidBackButton();
    // Make "the app came back" a first-class signal: React Query's own listener
    // misses the native resume, and focus alone never reaches an unmounted
    // screen. See appResume.ts - this is what ends the 80-minute blackout.
    installAppResume(queryClient);
  }, [queryClient]);

  // Register this device for push once authenticated; unregister on logout.
  // No-op until Firebase is configured (see usePushRegistration).
  usePushRegistration();

  // Live reservation updates over SSE for the selected restaurant - keeps the
  // Reservations view fresh the instant a booking arrives or changes.
  useReservationsRealtime();

  // Live order updates over SSE - keeps the Orders view fresh the instant a
  // storefront order arrives, is cancelled by the customer, or the sweeper acts.
  useOrdersRealtime();

  const showApp = isAuthenticated && !!selectedRestaurantId;

  return (
    <App {...f7params} colors={brandColors()}>
      {showApp ? (
        <BootstrapGate restaurantId={selectedRestaurantId}>
          <SuspensionGate restaurantId={selectedRestaurantId}>
            {isMobile ? (
              /* ── Mobile: bottom tab bar ──────────────────────────── */
              <MobileTabs />
            ) : (
              /* ── Tablet/Desktop: sidebar (iOS) or rail (Android) + main view ── */
              <div
                className={`app-desktop-layout${isIosChrome ? '' : ' app-desktop-layout--rail'}`}
              >
                {isIosChrome ? (
                  <aside
                    className={`app-desktop-sidebar${sidebarCollapsed ? ' app-desktop-sidebar--collapsed' : ''}`}
                  >
                    <Sidebar />
                  </aside>
                ) : (
                  <aside className="app-desktop-rail">
                    <NavigationRail />
                  </aside>
                )}
                <main className="app-desktop-main">
                  {/* No `browserHistory`: it's incompatible with React StrictMode's
                      dev double-mount (F7 rebuilds the page stack on history
                      restore and mis-assigns `page-current` - upstream #4278), which
                      broke direct /settings loads + HMR reloads. Mobile already runs
                      without it; the in-app nav is the source of truth. */}
                  <View main url="/reservation" />
                </main>
              </div>
            )}
            {/* Settings: a full-screen sheet OVER the whole app, DESKTOP ONLY (on
                mobile, Settings is the `view-settings` tab page). `tabletFullscreen`
                makes the popup fill the screen on desktop too, and F7 slides it up on
                open / down on close - covering the sidebar and revealing the app
                intact on dismiss. Kept OUTSIDE the isMobile branch so a resize to
                mobile closes it cleanly via `opened=false` (F7 tears down its
                relocated DOM) instead of an abrupt unmount stranding it on top of the
                mobile UI. The shell stays mounted through the close animation. */}
            <Popup
              className="settings-popup"
              tabletFullscreen
              opened={settingsOpen && !isMobile}
              onPopupClosed={() => {
                setSettingsOpen(false);
                setSettingsMounted(false);
              }}
            >
              {(settingsOpen || settingsMounted) && !isMobile && <SettingsPage />}
            </Popup>
            {/* AI Assistant chat lives at the app shell so the sidebar/rail quick
                action opens it from anywhere - not just inside Settings. Mounted
                once (it self-renders only when the store's isOpen flips). */}
            {aiAvailable && <AssistantSheet />}
          </SuspensionGate>
        </BootstrapGate>
      ) : (
        <View main url={isAuthenticated ? '/select-restaurant' : '/login'} />
      )}
    </App>
  );
}

export default AppComponent;
